import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as api from '../api/api';
import { buildSummaryParams } from '../utils/insights/paramBuilders';

/**
 * Hook para gestionar la verificación y generación de resúmenes
 * @param {string} summaryName - Nombre del resumen
 * @param {Object} dateRange - Rango de fechas
 * @param {Array} selectedItems - Items seleccionados de la tabla
 * @param {Array} tableItems - Items de la tabla (para validación)
 * @param {string|null} selectedSupervisor - Supervisor seleccionado
 * @param {Array} selectedAgents - Agentes seleccionados
 * @returns {Object} { generatingSummary, verifying, verificationResult, processParams, generateSummary, processAfterConfirmation, isCreateSummaryButtonEnabled, showConfirmationModal, setShowConfirmationModal, showPostSuccessModal, setShowPostSuccessModal, postRequestResult, setPostRequestResult }
 */
export function useSummaryGeneration(
  summaryName,
  dateRange,
  selectedItems,
  tableItems,
  selectedSupervisor,
  selectedAgents
) {
  const history = useHistory();
  const { t } = useTranslation();
  
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [processParams, setProcessParams] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showPostSuccessModal, setShowPostSuccessModal] = useState(false);
  const [postRequestResult, setPostRequestResult] = useState("");

  const isCreateSummaryButtonEnabled = () => {
    // Se requiere el nombre del resumen
    if (!summaryName || summaryName.trim() === "") return false;
    
    // Se requiere (rango de fechas O al menos un item seleccionado) y al menos una llamada en la tabla
    const hasDateRange = dateRange !== null && dateRange.startDate && dateRange.endDate;
    const hasSelectedItems = selectedItems.length > 0;
    const hasTableItems = tableItems.length > 0;
    
    return (hasDateRange || hasSelectedItems) && hasTableItems && !generatingSummary && !verifying;
  };

  const generateSummary = async () => {
    // Se requiere el nombre del resumen y (rango de fechas O items seleccionados)
    if (!summaryName || summaryName.trim() === "") return;
    if (!dateRange && selectedItems.length === 0) return;

    try {
      // Iniciar verificación
      setVerifying(true);
      setGeneratingSummary(true);
      
      // Preparar parámetros base
      const baseParams = {};
      
      // Si hay items seleccionados, usar solo esos (no filtros)
      if (selectedItems.length > 0) {
        // No incluir fechas cuando hay selección específica
        baseParams.selectedJobNames = selectedItems.map(item => item.jobName).filter(jobName => jobName);
      } else {
        // Si no hay selección, usar filtros (fechas obligatorias)
        if (!dateRange) return;
        baseParams.startDate = dateRange.startDate;
        baseParams.endDate = dateRange.endDate;
      }
      
      // Preparar parámetros para verificación
      const verifyParams = buildSummaryParams(baseParams, {
        summaryName,
        selectedJobNames: selectedItems.map(item => item.jobName).filter(jobName => jobName),
        supervisor: selectedSupervisor,
        agents: selectedAgents,
        includeSelectedJobNames: true
      });
      
      console.log("Verificando datos con parámetros:", verifyParams);
      
      // Paso 1: Verificar si hay datos disponibles
      const verifyResponse = await api.verifyData(verifyParams);
      console.log("Respuesta de verificación:", verifyResponse);
      
      // Si no se encontraron datos, mostrar mensaje y detener
      if (!verifyResponse.found) {
        setPostRequestResult(verifyResponse.message || "No se encontraron datos que coincidan con los filtros");
        setShowPostSuccessModal(true);
        return;
      }
      
      // Si hay datos, guardar el resultado y mostrar modal de confirmación
      setVerificationResult(verifyResponse);
      
      // Preparar parámetros para procesar y guardarlos para confirmar
      const params = buildSummaryParams(baseParams, {
        summaryName,
        selectedJobNames: selectedItems.map(item => item.jobName).filter(jobName => jobName),
        supervisor: selectedSupervisor,
        agents: selectedAgents,
        includeSelectedJobNames: true
      });
      setProcessParams(params);
      
      // Mostrar modal de confirmación
      setShowConfirmationModal(true);
      
    } catch (error) {
      console.error('Error en verificación:', error);
      setPostRequestResult(error.message || "Error al verificar datos");
      setShowPostSuccessModal(true);
    } finally {
      setVerifying(false);
      setGeneratingSummary(false);
    }
  };

  const processAfterConfirmation = async () => {
    try {
      setGeneratingSummary(true);
      
      console.log("Generando resumen con parámetros confirmados:", processParams);
      
      // Usar postSummary para generar el resumen
      const response = await api.postSummary(processParams);
      console.log("Respuesta al generar resumen:", response);
      
      // Mostrar resultado en modal si hay un mensaje específico
      if (response && response.message) {
        setPostRequestResult(response.message);
        setShowPostSuccessModal(true);
      }
      
      // Para análisis globales, no actualizamos la tabla de elementos individuales
      // El resumen se guarda automáticamente en el historial por el backend
      console.log("Análisis global completado, resultado guardado en historial");

      // Navegar a la pantalla de historial después de generar
      setTimeout(() => {
        history.push('/history-insights-calls');
      }, 1000);
      
    } catch (error) {
      console.error('Error generating summary:', error);
      // Mostrar error en modal
      setPostRequestResult(error.message || t("insights.postError"));
      setShowPostSuccessModal(true);
    } finally {
      setGeneratingSummary(false);
      setShowConfirmationModal(false);
    }
  };

  return {
    generatingSummary,
    verifying,
    verificationResult,
    processParams,
    generateSummary,
    processAfterConfirmation,
    isCreateSummaryButtonEnabled,
    showConfirmationModal,
    setShowConfirmationModal,
    showPostSuccessModal,
    setShowPostSuccessModal,
    postRequestResult,
    setPostRequestResult
  };
}

